import * as Enums from '../enums/index.js';
import BaseballResultsProcessor from '../entities/BaseballResultsProcessor.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';
import { ResultPair } from '../valueObjects/index.js';
import { PlateDisciplineStatsCalculator, BattingStatsCalculator, UniversalPlateDisciplineStatsCalculator, UniversalBattingStatsCalculator } from '../calculators/index.js';
import FilteredBattingResultsProvider from './FilteredBattingResultsProvider.js';
import EnumValueProvider from './EnumValueProvider.js';

// TODO - add average speed and launch angle stats
class CumulativeBattingSummaryDataProvider {
    constructor(plateDisciplineStatsCalculator, battingStatsCalculator, customResultsProcessor, filteredBattingResultsProvider) {
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
        this.plateDisciplineStatsCalculator = plateDisciplineStatsCalculator || new UniversalPlateDisciplineStatsCalculator();
        this.battingStatsCalculator = battingStatsCalculator || new UniversalBattingStatsCalculator();
        this.filteredBattingResultsProvider = filteredBattingResultsProvider || new FilteredBattingResultsProvider(this.resultsProcessor);

        if (!(this.resultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }

        if (!(this.plateDisciplineStatsCalculator instanceof PlateDisciplineStatsCalculator)) {
            throw new TypeError('plateDisciplineCalculator must be an instance inheriting from PlateDisciplineStatsCalculator class');
        }

        if (!(this.battingStatsCalculator instanceof BattingStatsCalculator)) {
            throw new TypeError('battingStatsCalculator must be an instance inheriting from BattingStatsCalculator class');
        }

        if (!(this.filteredBattingResultsProvider instanceof FilteredBattingResultsProvider)) {
            throw new TypeError('filteredBattingResultsProvider must be an instance inheriting from FilteredBattingResultsProvider class');
        }
    }

    defaultFilters() {
        return this.filteredBattingResultsProvider.defaultFilters();
    }

    calculateBattingStats(resultPairs) {
        const plateDiscipline = this.plateDisciplineStatsCalculator.calculatePlateDiscipline(
            this.filteredBattingResultsProvider.extractPitches(resultPairs)
        );
        if (!plateDiscipline) {
            // TODO - maybe log only ? we still can get speeds etc
            throw new Error('Could not calculate plate discipline stats for given set of results');
        }

        const battingStats = this.battingStatsCalculator.calculateHitterStats(resultPairs);
        if (!battingStats) {
            // TODO - maybe log only ? we still can get speeds etc
            throw new Error('Could not calculate batting stats for given set of results');
        }

        const performanceStats = this.calculatePerformanceStats(
            this.filteredBattingResultsProvider.extractHits(resultPairs)
        );
        if (!performanceStats) {
            // TODO - maybe log only ? we still can get speeds etc
            throw new Error('Could not calculate batting performance stats for given set of results');
        }

        return Object.assign(plateDiscipline, battingStats, performanceStats);
    }

    calculatePerformanceStats(hitsOfBatter) {
        const performanceStats = {
            [Enums.BattingStats.ExitSpeedAvg.key]: NaN,
            [Enums.BattingStats.ExitSpeedMax.key]: NaN,
            [Enums.BattingStats.HitLaunchVAvg.key]: NaN,
            [Enums.BattingStats.CarryMax.key]: NaN,
        };

        const resultsMapped = this.resultsProcessor.mapCollectionProperties(hitsOfBatter, [
            Enums.ResultData.getDataPath(Enums.HitData.ExitSpeed.key),
            Enums.ResultData.getDataPath(Enums.HitData.LaunchV.key),
            Enums.ResultData.getDataPath(Enums.HitData.CarryDistance.key),
        ]);

        const stats = this.resultsProcessor.calcStats(
            [Enums.StatType.Avg.key, Enums.StatType.StDev.key, Enums.StatType.Max.key], resultsMapped, 
            // TODO - simplify it...
            [ [ [], [ Enums.HitData.ExitSpeed.key, Enums.HitData.LaunchV.key, Enums.HitData.CarryDistance.key ] ] ], false
        );

        if (stats) {
            if (stats.hasOwnProperty(Enums.HitData.ExitSpeed.key)){
                performanceStats[Enums.BattingStats.ExitSpeedAvg.key] = stats[Enums.HitData.ExitSpeed.key][Enums.StatType.Avg.key];
                performanceStats[Enums.BattingStats.ExitSpeedMax.key] = stats[Enums.HitData.ExitSpeed.key][Enums.StatType.Max.key];
            }

            if (stats.hasOwnProperty(Enums.HitData.LaunchV.key)){
                performanceStats[Enums.BattingStats.HitLaunchVAvg.key] = stats[Enums.HitData.LaunchV.key][Enums.StatType.Avg.key];
            }

            if (stats.hasOwnProperty(Enums.HitData.CarryDistance.key)){
                performanceStats[Enums.BattingStats.CarryMax.key] = stats[Enums.HitData.CarryDistance.key][Enums.StatType.Max.key];
            }
        }

        return performanceStats;
    }

    determineSummaryForBatter(batterId, pitchesAgainstBatter, hitsOfBatter) {
        const resultPairs = this.resultsProcessor.matchPitchesAndHits([].concat(pitchesAgainstBatter, hitsOfBatter));

        const pairsPerPitchType = this.resultsProcessor.groupData(
            resultPairs, [[ResultPair.getPitchProperty()].concat(Enums.ResultData.getDataPath(Enums.PitchData.Classification.key))],
            [], true, Enums.PitchType.Unidentified.key
        );

        const resultIds = pitchesAgainstBatter.map(r => r[Enums.ResultData.Id.key]).concat(
            hitsOfBatter.map(r => r[Enums.ResultData.Id.key])
        );

        const summaryPerPitchType = [];
        for (const pitchType in pairsPerPitchType) {
            if (pairsPerPitchType.hasOwnProperty(pitchType)) {
                // verify against enum
                const pitchTypeEnumValue = EnumValueProvider.getValue(pitchType, Enums.PitchType, Enums.PitchType.Unidentified.key);
                if (pitchTypeEnumValue.key !== pitchType) {
                    // merge with unidentified
                    if (pairsPerPitchType.hasOwnProperty(pitchTypeEnumValue.key)) {
                        // merge
                        pairsPerPitchType[pitchTypeEnumValue.key] = pairsPerPitchType[pitchTypeEnumValue.key].concat(pairsPerPitchType[pitchType]);
                        continue;
                    }
                }

                const pairsForPitchType = pairsPerPitchType[pitchType];
                summaryPerPitchType.push(Object.assign({
                    pitchType: pitchTypeEnumValue.key, 
                    resultIds,
                }, this.calculateBattingStats(pairsForPitchType)));
            }
        }

        const summaryForBatter = {
            batterId,
            pitchTypes: summaryPerPitchType,
            summary: this.calculateBattingStats(resultPairs),
        };
    
        return summaryForBatter;
    }

    getDataForHittingSummary (
        results,
        {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        } = {}
    ) {
        const filteredPairs = this.filteredBattingResultsProvider.filterResults(results, {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        });

        const filteredPitches = this.filteredBattingResultsProvider.extractPitches(filteredPairs);
        const filteredHits = this.filteredBattingResultsProvider.extractHits(filteredPairs);

        const pitchesPerBatter = this.resultsProcessor.groupData(filteredPitches, [Enums.ResultData.BatterTagId.key]);
        const hitsPerBatter = this.resultsProcessor.groupData(filteredHits, [Enums.ResultData.TagId.key]);
        const summaryPerHitter = [];
        const allBatters = Object.keys(pitchesPerBatter).concat(Object.keys(hitsPerBatter));

        // now we must match it...
        // TODO - what about order ? rara case but still possible
        const processedBatters = [];
        for (let i = 0; i < allBatters.length; i++) {
            const batterId = allBatters[i];

            if (processedBatters.indexOf(batterId) > -1) {
                continue;
            }

            processedBatters.push(batterId);

            const pitchesAgainstBatter = pitchesPerBatter[batterId] || [];
            const hitsOfBatter = hitsPerBatter[batterId] || [];

            const summaryForHitter = this.determineSummaryForBatter(
                batterId, pitchesAgainstBatter, hitsOfBatter
            );
            
            summaryPerHitter.push(summaryForHitter);
        }

        return summaryPerHitter;
    };

    listUniqueParametersForHittingSummary(
        results, {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        } = {},
        pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult
    ) {
        pitchParameters = pitchParameters || [
            // tagging context
            [Enums.ResultData.BatterTagId.key],
            [Enums.ResultData.TagId.key],
            Enums.ResultData.getDataPath(Enums.PitchData.SwingHandedness.key),
            Enums.ResultData.getDataPath(Enums.PitchData.Result.key),
            Enums.ResultData.getDataPath(Enums.PitchData.Classification.key),
            //Enums.ResultData.getDataPath(Enums.PitchData.Mechanics.key),
            Enums.ResultData.getDataPath(Enums.PitchData.Handedness.key),
            // scoring context
            Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayOuts.key),
            Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayBalls.key),
            Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayStrikes.key),
            Enums.ResultData.getDataPath(Enums.PitchData.BeforePlayRunnersOn.key),
        ];

        hitParameters = hitParameters || [
            [Enums.ResultData.TagId.key],
            Enums.ResultData.getDataPath(Enums.HitData.SwingHandedness.key),
            // Enums.ResultData.getDataPath(Enums.PitchData.Handedness.key), // right now we can't use it
        ];

        // it contains map between pitchParameters and hitParameters, so basically n item from pitchParameters will be merged with item from hitParameters at index equal to value at n
        pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
            0, undefined, 1
        ];

        const filteredPairs = this.filteredBattingResultsProvider.filterResults(results, {
            // tagging context
            pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
            // scoring context
            outs, balls, strikes, runnersOn,
        });

        const filteredPitches = this.filteredBattingResultsProvider.extractPitches(filteredPairs);
        const filteredHits = this.filteredBattingResultsProvider.extractHits(filteredPairs);
        
        return UniqueParametersForPitchAndHitProvider(pitchParameters, hitParameters, pitchToHitParamsToMerge, filteredPitches, filteredHits, this.resultsProcessor, combinedPitchResult);
    }
}

export default CumulativeBattingSummaryDataProvider;